import { I18nData } from '../../../hooks/api/useI18n';
import { ErrorObject } from '../../../types/errorhandling.types';

export interface I18nIconItem {
  name?: string;
  description?: string;
  url?: string;
  icon?: string;
}

export interface I18nTabProps {
  currentLocale: string;
  i18nData: I18nData | undefined;
  publicI18nData?: I18nData;
  onI18nDataChange: (data: I18nData) => void;
  fieldErrors?: ErrorObject;
}

const isHashValue = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};


const isStringValue = (value: unknown): value is string => {
  return typeof value === 'string';
};

export const getAvailableLocales = (i18nData?: I18nData): string[] => {
  return i18nData ? Object.keys(i18nData).sort() : ['en'];
};

export const hasTextItems = (locale: string, i18nData?: I18nData): boolean => {
  if (!i18nData || !i18nData[locale]) return true; // Default to showing tab if no data

  return Object.values(i18nData[locale]).some((value) => isStringValue(value));
};

export const hasIconItems = (locale: string, i18nData?: I18nData): boolean => {
  if (!i18nData || !i18nData[locale]) return true; // Default to showing tab if no data

  return Object.values(i18nData[locale]).some((value) => isHashValue(value));
};

interface HashKeyItem {
  category: string;
  key: string;
  editableFields: {
    icon: boolean;
    description: boolean;
    name: boolean;
  };
}

export const getHashKeys = (
  locale: string,
  i18nData?: I18nData,
  publicI18nData?: I18nData
): HashKeyItem[] => {
  if (!publicI18nData) return [];

  const items = new Map<string, HashKeyItem>();

  const processLocale = (localeData: Record<string, unknown>) => {
    if (!localeData || typeof localeData !== 'object') return;

    Object.entries(localeData).forEach(([category, categoryData]) => {
      if (!isHashValue(categoryData)) return;

      Object.entries(categoryData).forEach(([key, value]) => {
        if (!isHashValue(value)) return;

        const itemKey = `${category}-${key}`;
        if (items.has(itemKey)) return;

        const privateLocaleData = i18nData?.[locale];
        const privateCategoryData = privateLocaleData?.[category] as
          | Record<string, unknown>
          | undefined;
        const privateItem = privateCategoryData?.[key] as
          | Record<string, unknown>
          | undefined;
        items.set(itemKey, {
          category,
          key,
          editableFields: {
            icon: privateItem?.icon !== undefined,
            description: privateItem?.description !== undefined,
            name: privateItem?.name !== undefined,
          },
        });
      });
    });
  };

  if (publicI18nData[locale]) processLocale(publicI18nData[locale]);
  Object.values(publicI18nData).forEach(processLocale);

  return Array.from(items.values()).sort((a, b) =>
    a.category !== b.category
      ? a.category.localeCompare(b.category)
      : a.key.localeCompare(b.key)
  );
};

export const updateI18nValue = (
  i18nData: I18nData | undefined,
  locale: string,
  key: string,
  value: unknown,
  category?: string
): I18nData => {
  const updatedData = { ...(i18nData || {}) };

  // Ensure locale exists
  updatedData[locale] = updatedData[locale] ? { ...updatedData[locale] } : {};

  // Handle non-category (text) updates
  if (!category) {
    updatedData[locale][key] = value;
    return updatedData;
  }

  // Handle category (icon) updates
  const currentCategoryData = updatedData[locale][category] as
    | Record<string, unknown>
    | undefined;
  updatedData[locale][category] = currentCategoryData
    ? { ...currentCategoryData }
    : {};

  if (typeof value === 'object' && value !== null) {
    const currentLocaleData = i18nData?.[locale];
    const currentCategory = currentLocaleData?.[category] as
      | Record<string, unknown>
      | undefined;
    const currentValue = currentCategory?.[key] || {};

    (updatedData[locale][category] as Record<string, unknown>)[key] = {
      ...currentValue,
      ...value,
    };
  } else {
    (updatedData[locale][category] as Record<string, unknown>)[key] = value;
  }

  return updatedData;
};

export function getI18nValue(
  i18nData: I18nData | undefined,
  locale: string,
  key: string,
  category: string,
  publicI18nData?: I18nData
): I18nIconItem;
export function getI18nValue(
  i18nData: I18nData | undefined,
  locale: string,
  key: string,
  category?: undefined,
  publicI18nData?: I18nData
): string;
export function getI18nValue(
  i18nData: I18nData | undefined,
  locale: string,
  key: string,
  category?: string,
  publicI18nData?: I18nData
): I18nIconItem | string {
  // Handle text items (no category)
  if (!category) {
    const privateValue = i18nData?.[locale]?.[key];
    if (privateValue !== undefined) return privateValue as string;
    return (publicI18nData?.[locale]?.[key] || '') as string;
  }

  // Handle icon items (with category)
  const publicLocaleData = publicI18nData?.[locale];
  const publicCategoryData = publicLocaleData?.[category] as
    | Record<string, unknown>
    | undefined;
  const publicValue = publicCategoryData?.[key] || {};

  const privateLocaleData = i18nData?.[locale];
  const privateCategoryData = privateLocaleData?.[category] as
    | Record<string, unknown>
    | undefined;
  const privateValue = privateCategoryData?.[key];

  if (
    !privateValue ||
    typeof privateValue !== 'object' ||
    typeof publicValue !== 'object'
  ) {
    return publicValue as I18nIconItem;
  }

  return { ...publicValue, ...privateValue } as I18nIconItem;
}

const formatKeyLabel = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

export const getCategoryDisplayName = (category: string): string => {
  switch (category) {
    case 'payments':
      return 'Payment';
    case 'licenses':
      return 'License';
    default:
      return formatKeyLabel(category);
  }
};

